import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFounderData, saveFounderData, FounderData } from '../services/founderService';
import { uploadImageToImgBB } from '../services/imgbbService';

const AdminDashboardPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [founderData, setFounderData] = useState<FounderData>({
    name: 'Marcus Veltrix',
    picture: '/images/loom.jpeg',
    title: 'Founder & CEO',
    bio: `Marcus Veltrix founded Veltrix Apparel in Manhattan with a simple mission: to give men one destination for premium suits, football jerseys, and training kits — without compromise on fit, quality, or style.

After years in menswear and sportswear on both sides of the Atlantic, Marcus saw a gap: guys who wanted a sharp suit for Monday and a proper jersey for the weekend had to chase multiple brands. In 2015 he opened the first Veltrix Apparel showroom in Manhattan, combining tailored formal wear with performance sportswear and custom team kit under one roof.

Today Veltrix Apparel serves professionals, teams, and everyday guys who care about how they look and perform. From boardroom-ready suits and blazers to match-day jerseys and training kits, every piece is selected or built to meet the standards of the modern man.

Marcus and the team are based in Manhattan, where they offer fittings, custom orders, and bulk team kit. Whether you need one perfect suit or a full squad in your colors, Veltrix Apparel is built for you.`
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    // Load founder data from Firebase (with localStorage fallback)
    const loadData = async () => {
      try {
        const data = await getFounderData();
        if (data) {
          setFounderData(data);
        }
      } catch (error) {
        console.error('Error loading founder data:', error);
        // Fallback to localStorage if Firebase fails
        const savedData = localStorage.getItem('founder_data');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            setFounderData(parsed);
          } catch (e) {
            console.error('Error parsing localStorage data:', e);
          }
        }
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const submitButton = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Saving...';
    }
    try {
      // Save to Firebase (always saves to localStorage first)
      const result = await saveFounderData(founderData);
      setMessage({ 
        type: result.success ? 'success' : 'error', 
        text: result.message 
      });
    } catch (error) {
      console.error('Unexpected error saving:', error);
      setMessage({ 
        type: 'error', 
        text: 'An unexpected error occurred. Data has been saved locally.' 
      });
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Save changes';
      }
    }
    
    // Clear message after 5 seconds
    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size must be less than 5MB.' });
      return;
    }

    setUploadingImage(true);
    setMessage(null);

    try {
      const base64String = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Error reading image file.'));
        reader.readAsDataURL(file);
      });

      const imageUrl = await uploadImageToImgBB(base64String);
      setFounderData((prev) => ({ ...prev, picture: imageUrl }));
      setMessage({ type: 'success', text: 'Image uploaded to ImgBB. Save changes to update the About page.' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Image upload failed.',
      });
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setFounderData({ ...founderData, picture: '/images/loom.jpeg' });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-2xl font-semibold text-slate-900">Admin dashboard</h1>
            <p className="text-sm text-slate-600 mt-0.5">Manage founder information for the About page</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Logout
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 lg:p-8">
          <h2 className="font-display text-lg font-semibold text-slate-900 mb-6">Edit founder information</h2>

          {message && (
            <div
              className={`mb-6 px-4 py-3 rounded-lg text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-100 text-green-800'
                  : 'bg-red-50 border border-red-100 text-red-800'
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">Founder name</label>
              <input
                type="text"
                id="name"
                value={founderData.name}
                onChange={(e) => setFounderData({ ...founderData, name: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                placeholder="Founder name"
                required
              />
            </div>

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-slate-700 mb-1.5">Title</label>
              <input
                type="text"
                id="title"
                value={founderData.title}
                onChange={(e) => setFounderData({ ...founderData, title: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                placeholder="e.g. Founder & CEO"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Picture</label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className={`flex-1 ${uploadingImage ? 'pointer-events-none opacity-70' : 'cursor-pointer'}`}>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                    <div className="w-full px-4 py-3 rounded-lg border-2 border-dashed border-slate-200 hover:border-slate-300 text-center text-slate-600 text-sm transition-colors">
                      {uploadingImage ? 'Uploading to ImgBB…' : 'Upload image (max 5MB)'}
                    </div>
                  </label>
                  {founderData.picture && founderData.picture !== '/images/loom.jpeg' && (
                    <button type="button" onClick={handleRemoveImage} className="px-4 py-3 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">
                      Remove
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={founderData.picture.startsWith('data:') ? '' : founderData.picture}
                  onChange={(e) => setFounderData({ ...founderData, picture: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent text-sm"
                  placeholder="Or enter image URL (e.g. /images/loom.jpeg)"
                />
              </div>
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-slate-700 mb-1.5">Bio</label>
              <textarea
                id="bio"
                value={founderData.bio}
                onChange={(e) => setFounderData({ ...founderData, bio: e.target.value })}
                rows={10}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-y"
                placeholder="Founder biography (paragraphs shown separately on About page)"
                required
              />
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
              <h3 className="text-sm font-medium text-slate-900 mb-3">Preview</h3>
              <div className="flex items-center gap-4">
                <img
                  src={founderData.picture}
                  alt={founderData.name}
                  className="w-16 h-16 object-cover rounded-lg"
                  onError={(e) => { (e.target as HTMLImageElement).src = '/images/loom.jpeg'; }}
                />
                <div>
                  <div className="font-display font-semibold text-slate-900">{founderData.name}</div>
                  <div className="text-sm text-slate-600">{founderData.title}</div>
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-500 line-clamp-2">{founderData.bio.substring(0, 150)}…</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                className="flex-1 py-3 rounded-lg bg-midnight text-white text-sm font-medium hover:opacity-90 transition-opacity border-2 border-midnight shadow-sm"
              >
                Save changes
              </button>
              <button
                type="button"
                onClick={() => navigate('/about')}
                className="py-3 px-6 rounded-lg border-2 border-charcoal bg-white text-charcoal text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                View About page
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;

